import { useState, useEffect } from 'react'
import { syncApi, qboApi } from '../services/api'
import Button from '../components/common/Button'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { api } from '../services/api'

export default function SyncDashboard() {
    const [loading, setLoading] = useState(true)
    const [status, setStatus] = useState(null)
    const [qboStatus, setQboStatus] = useState(null)
    const [pendingTimesheets, setPendingTimesheets] = useState([])
    const [selectedTimesheets, setSelectedTimesheets] = useState([])
    const [selectedJob, setSelectedJob] = useState(null)
    const [unsubmittedTimesheets, setUnsubmittedTimesheets] = useState([])
    const [loadingJob, setLoadingJob] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const [syncingAction, setSyncingAction] = useState(null)
    const navigate = useNavigate()

    const isQboConnected = qboStatus?.isConnected && !qboStatus?.isRefreshTokenExpired

    useEffect(() => {
        loadAll()
        const statusInterval = setInterval(loadQboStatus, 60000)
        const syncInterval = setInterval(async () => {
            if (isQboConnected) {
                await qboApi.syncCustomers()
                await qboApi.syncEstimates()
                await loadPendingTimesheets()
            }
        }, 15 * 60 * 1000)
        return () => {
            clearInterval(statusInterval)
            clearInterval(syncInterval)
        }
    }, [isQboConnected])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const qbo = params.get('qbo')
        if (qbo === 'connected') {
            toast.success('Connected to QuickBooks Online!')
            window.history.replaceState({}, '', window.location.pathname)
            loadQboStatus()
        } else if (qbo === 'error') {
            const msg = params.get('message') || 'Connection failed'
            toast.error(`QBO Error: ${msg}`)
            window.history.replaceState({}, '', window.location.pathname)
        }
    }, [])

    async function loadAll() {
        await Promise.all([loadStatus(), loadQboStatus(), loadPendingTimesheets()])
        setLoading(false)
    }

    const openInspections = async () => {
        try {
            const data = await api.post('/api/auth/inspection-bypass')
            if (data.success) {
                window.open(data.url, '_blank')
            }
        } catch (error) {
            console.error('Failed to generate bypass token:', error)
        }
    }

    async function loadStatus() {
        try {
            const response = await syncApi.getStatus()
            if (response.success) {
                setStatus(response.data)
            }
        } catch (error) {
            console.error('Failed to load status:', error)
        }
    }

    async function loadQboStatus() {
        try {
            const response = await qboApi.getStatus()
            if (response.success) {
                setQboStatus(response.data)
            }
        } catch (error) {
            console.error('Failed to load QBO status:', error)
        }
    }

    async function loadPendingTimesheets() {
        try {
            const response = await syncApi.getPendingTimesheets()
            if (response.success) {
                setPendingTimesheets(response.timesheets || [])
            }
        } catch (error) {
            console.error('Failed to load pending timesheets:', error)
        }
    }

    async function handleQboConnect() {
        try {
            const response = await qboApi.connect()
            if (response.success && response.authUrl) {
                window.location.href = response.authUrl
            } else {
                toast.error('Failed to start QBO connection')
            }
        } catch (error) {
            toast.error('Error connecting to QBO')
        }
    }

    async function handleQboDisconnect() {
        if (!confirm('Disconnect from QuickBooks Online?')) return
        try {
            const response = await qboApi.disconnect()
            if (response.success) {
                toast.success('Disconnected from QuickBooks Online')
                setQboStatus(null)
            }
        } catch (error) {
            toast.error('Error disconnecting')
        }
    }

    async function handleQboSync(type) {
        setSyncing(true)
        setSyncingAction(type)
        try {
            let response
            if (type === 'all') {
                response = await qboApi.syncAll()
                if (response.success) {
                    toast.success('Full sync completed!')
                } else {
                    toast.error('Some sync operations failed')
                }
            } else if (type === 'customers') {
                response = await qboApi.syncCustomers()
                toast[response.success ? 'success' : 'error'](response.message)
            } else if (type === 'estimates') {
                response = await qboApi.syncEstimates()
                toast[response.success ? 'success' : 'error'](response.message)
            } else if (type === 'employees') {
                response = await qboApi.syncEmployees()
                toast[response.success ? 'success' : 'error'](response.message)
            }
            await Promise.all([loadPendingTimesheets(), loadStatus()])
        } catch (error) {
            toast.error(`Error syncing ${type}`)
        }
        setSyncing(false)
        setSyncingAction(null)
    }

    async function handleExport() {
        if (selectedTimesheets.length === 0) {
            toast.error('Please select timesheets to export')
            return
        }

        setSyncing(true)
        setSyncingAction('export')
        try {
            const response = await qboApi.exportTimesheets(selectedTimesheets)
            if (response.success) {
                toast.success(response.message || 'Export completed!')
                setSelectedTimesheets([])
                setSelectedJob(null)
                setUnsubmittedTimesheets([])
            } else {
                toast.error(response.message || 'Export failed')
            }
            await Promise.all([loadPendingTimesheets(), loadStatus()])
        } catch (error) {
            toast.error('Error exporting timesheets')
            await Promise.all([loadPendingTimesheets(), loadStatus()])
        }
        setSyncing(false)
        setSyncingAction(null)
    }

    const groupedJobs = pendingTimesheets.reduce((acc, ts) => {
        const key = `${ts.customer}|${ts.workOrderNumber}`
        if (!acc[key]) {
            acc[key] = {
                customer: ts.customer,
                workOrderNumber: ts.workOrderNumber,
                timesheets: []
            }
        }
        acc[key].timesheets.push(ts)
        return acc
    }, {})

    const jobs = Object.values(groupedJobs).sort((a, b) =>
        a.customer.localeCompare(b.customer)
    )

    const selectedJobData = selectedJob !== null ? jobs[selectedJob] : null

    function toggleTimesheet(uuid) {
        setSelectedTimesheets(prev =>
            prev.includes(uuid)
                ? prev.filter(id => id !== uuid)
                : [...prev, uuid]
        )
    }

    function selectAllInJob() {
        if (!selectedJobData) return
        const jobUuids = selectedJobData.timesheets.map(ts => ts.uuid)
        const allSelected = jobUuids.every(uuid => selectedTimesheets.includes(uuid))
        if (allSelected) {
            setSelectedTimesheets(prev => prev.filter(uuid => !jobUuids.includes(uuid)))
        } else {
            setSelectedTimesheets(prev => [...new Set([...prev, ...jobUuids])])
        }
    }

    async function handleSelectJob(index) {
        setSelectedJob(index)
        setUnsubmittedTimesheets([])
        const job = jobs[index]
        if (!job) return

        setLoadingJob(true)
        try {
            const response = await syncApi.getJobTimesheets(job.workOrderNumber)
            if (response.success && response.data?.unsubmitted) {
                setUnsubmittedTimesheets(response.data.unsubmitted)
            }
        } catch (error) {
            console.error('Failed to load job timesheets:', error)
        }
        setLoadingJob(false)
    }

    function getTypeBadge(type) {
        const types = {
            '0': { label: 'Timesheet', class: 'bg-blue-100 text-blue-800' },
            '1': { label: 'Repair', class: 'bg-purple-100 text-purple-800' },
            '2': { label: 'Packing', class: 'bg-green-100 text-green-800' },
            '3': { label: 'Internal', class: 'bg-gray-100 text-gray-800' },
            '4': { label: 'Pickup', class: 'bg-yellow-100 text-yellow-800' },
            '5': { label: 'Vendor', class: 'bg-orange-100 text-orange-800' },
        }
        const t = types[type] || { label: 'Unknown', class: 'bg-gray-100' }
        return <span className={`text-xs px-2 py-0.5 rounded ${t.class}`}>{t.label}</span>
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }


    return (
        <div>
            {/* Header */}
            <div className="page-header">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h1 className="page-title">Sync Dashboard</h1>
                    <div className="grid grid-cols-3 gap-2">
                        <Button variant="secondary" onClick={openInspections}>
                            📋 Inspections
                        </Button>
                        <Button variant="secondary" onClick={() => navigate('/company-settings')}>
                            🏢 Company Info
                        </Button>
                        <Button variant="secondary" onClick={() => navigate('/')}>
                            ← Dashboard
                        </Button>
                    </div>
                </div>
            </div>

            {/* Status Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-md p-4">
                    <p className="text-sm text-gray-500 uppercase">QuickBooks Online</p>
                    {isQboConnected ? (
                        <>
                            <p className="text-2xl font-bold text-green-500">Connected</p>
                            <p className="text-xs text-gray-400 mt-1">{qboStatus.companyName || qboStatus.realmId}</p>
                        </>
                    ) : (
                        <>
                            <p className="text-2xl font-bold text-red-500">Not Connected</p>
                            <p className="text-xs text-gray-400 mt-1">Click Connect below</p>
                        </>
                    )}
                </div>

                <div className="bg-white rounded-xl shadow-md p-4">
                    <p className="text-sm text-gray-500 uppercase">QBO Connection</p>
                    {isQboConnected ? (() => {
                        const expires = qboStatus.refreshTokenExpires ? new Date(qboStatus.refreshTokenExpires) : null
                        const daysLeft = expires ? Math.ceil((expires - new Date()) / (1000 * 60 * 60 * 24)) : null

                        if (daysLeft === null) {
                            return (
                                <>
                                    <p className="text-2xl font-bold text-green-500">Active</p>
                                    <p className="text-xs text-gray-400 mt-1">No action needed</p>
                                </>
                            )
                        }
                        if (daysLeft <= 0) {
                            return (
                                <>
                                    <p className="text-2xl font-bold text-red-500">Expired</p>
                                    <p className="text-xs text-red-600 mt-1">Click Connect to restore</p>
                                </>
                            )
                        }
                        if (daysLeft <= 7) {
                            return (
                                <>
                                    <p className="text-2xl font-bold text-red-500">{daysLeft} day{daysLeft === 1 ? '' : 's'} left</p>
                                    <p className="text-xs text-red-600 mt-1">Run a sync or reconnect soon</p>
                                </>
                            )
                        }
                        if (daysLeft <= 30) {
                            return (
                                <>
                                    <p className="text-2xl font-bold text-yellow-500">{daysLeft} days idle</p>
                                    <p className="text-xs text-gray-500 mt-1">Any sync will refresh this</p>
                                </>
                            )
                        }
                        return (
                            <>
                                <p className="text-2xl font-bold text-green-500">Active</p>
                                <p className="text-xs text-gray-400 mt-1">No action needed</p>
                            </>
                        )
                    })() : (
                        <>
                            <p className="text-2xl font-bold text-gray-400">N/A</p>
                            <p className="text-xs text-gray-400 mt-1">Connect first</p>
                        </>
                    )}
                </div>

                <div className="bg-white rounded-xl shadow-md p-4">
                    <p className="text-sm text-gray-500 uppercase">Pending Export</p>
                    <p className="text-2xl font-bold text-rdi-blue">{pendingTimesheets.length}</p>
                    <p className="text-xs text-gray-400 mt-1">Timesheets ready</p>
                </div>

                <div className="bg-white rounded-xl shadow-md p-4">
                    <p className="text-sm text-gray-500 uppercase">Queue</p>
                    <p className="text-2xl font-bold text-rdi-blue">{status?.pendingCommands || 0}</p>
                    <p className="text-xs text-gray-400 mt-1">Commands pending</p>
                </div>
            </div>

            {/* QBO Connection Card */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">QuickBooks Online Connection</h2>
                    <div className="flex items-center gap-3">
                        {isQboConnected && (
                            <span className="flex items-center gap-2 text-sm text-green-600 font-medium">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                Connected to {qboStatus.companyName || 'QBO'}
                            </span>
                        )}
                        <Button variant="secondary" onClick={handleQboDisconnect} className="text-red-600 border-red-300 hover:bg-red-50">
                            Disconnect
                        </Button>
                        
                    </div>
                </div>

                {isQboConnected ? (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Button onClick={() => handleQboSync('all')} loading={syncingAction === 'all'} disabled={syncing}>
                                {syncingAction === 'all' ? 'Syncing All...' : 'Sync All'}
                            </Button>
                            <Button variant="secondary" onClick={() => handleQboSync('customers')} loading={syncingAction === 'customers'} disabled={syncing}>
                                {syncingAction === 'customers' ? 'Syncing...' : 'Sync Customers'}
                            </Button>
                            <Button variant="secondary" onClick={() => handleQboSync('estimates')} loading={syncingAction === 'estimates'} disabled={syncing}>
                                {syncingAction === 'estimates' ? 'Syncing...' : 'Sync Estimates'}
                            </Button>
                            <Button variant="secondary" onClick={() => handleQboSync('employees')} loading={syncingAction === 'employees'} disabled={syncing}>
                                {syncingAction === 'employees' ? 'Syncing...' : 'Sync Employees'}
                            </Button>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <Button variant="secondary" size="sm" onClick={loadAll}>
                                🔄 Refresh
                            </Button>   
                        </div>
                    </>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-gray-500 mb-4">Connect to QuickBooks Online to sync customers and export timesheets.</p>
                        <Button onClick={handleQboConnect}>Connect to QuickBooks Online</Button>
                    </div>
                )}
            </div>

            {/* Two Panel Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Jobs Panel */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="bg-rdi-blue text-white px-4 py-3 flex justify-between items-center">
                        <span className="font-semibold">Pending Jobs Ready</span>
                        <span className="bg-white/20 px-2 py-0.5 rounded text-sm">{jobs.length}</span>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {jobs.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">No pending jobs</div>
                        ) : (
                            <ul>
                                {jobs.map((job, index) => (
                                    <li
                                        key={`${job.customer}|${job.workOrderNumber}`}
                                        onClick={() => handleSelectJob(index)}
                                        className={`p-4 border-b cursor-pointer transition-colors ${selectedJob === index ? 'bg-blue-50 border-l-4 border-l-rdi-blue' : 'hover:bg-gray-50'}`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="font-medium text-gray-800">{job.customer}</p>
                                                <p className="text-sm text-gray-500">Job #{job.workOrderNumber}</p>
                                            </div>
                                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-semibold">
                                                {job.timesheets.length}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Timesheets Panel */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="bg-rdi-blue text-white px-4 py-3 flex justify-between items-center">
                        <span className="font-semibold">Pending Timesheets for Export</span>
                        <div className="flex items-center gap-2">
                            {unsubmittedTimesheets.length > 0 && (
                                <span className="bg-amber-400 text-amber-900 px-2 py-0.5 rounded text-xs font-semibold">
                                    {unsubmittedTimesheets.length} unsubmitted
                                </span>
                            )}
                            <span className="bg-white/20 px-2 py-0.5 rounded text-sm">
                                {selectedJobData?.timesheets.length || 0}
                            </span>
                        </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {!selectedJobData ? (
                            <div className="text-center py-12 text-gray-500">
                                <p className="text-2xl mb-2">←</p>
                                <p>Select a job to view timesheets</p>
                            </div>
                        ) : (
                            <>
                                <div className="p-3 bg-gray-50 border-b flex items-center">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedJobData.timesheets.every(ts => selectedTimesheets.includes(ts.uuid))}
                                            onChange={selectAllInJob}
                                            className="w-4 h-4 mr-2"
                                        />
                                        <span className="font-medium">Select All</span>
                                    </label>
                                </div>
                                {unsubmittedTimesheets.length > 0 && (
                                    <div className="p-3 bg-amber-50 border-b border-amber-200">
                                        <div className="flex items-start gap-2">
                                            <span className="text-amber-600 text-lg leading-none mt-0.5">⚠️</span>
                                            <div>
                                                <p className="text-sm font-semibold text-amber-800">
                                                    {unsubmittedTimesheets.length} Unsubmitted Timesheet{unsubmittedTimesheets.length !== 1 ? 's' : ''}
                                                </p>
                                                <p className="text-xs text-amber-700 mt-1">
                                                    The following technicians have timesheets not yet submitted for this job:
                                                </p>
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    {[...new Set(unsubmittedTimesheets.map(ts => ts.technicianName))].map(name => (
                                                        <span key={name} className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                                                            {name}
                                                        </span>
                                                    ))}
                                                </div>
                                                <div className="mt-2 space-y-0.5">
                                                    {unsubmittedTimesheets.map(ts => (
                                                        <p key={ts.uuid} className="text-xs text-amber-600">
                                                            {ts.referenceNo} — {ts.technicianName} — {ts.month}
                                                        </p>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <ul>
                                    {selectedJobData.timesheets.map(ts => (
                                        <li key={ts.uuid} className="p-4 border-b hover:bg-gray-50">
                                            <label className="flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTimesheets.includes(ts.uuid)}
                                                    onChange={() => toggleTimesheet(ts.uuid)}
                                                    className="w-4 h-4 mr-3"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">{ts.referenceNo}</span>
                                                        {getTypeBadge(ts.tsType)}
                                                    </div>
                                                    <p className="text-sm text-gray-500">{ts.employeeName} — {ts.month}</p>
                                                </div>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Export Bar */}
            <div className="bg-white rounded-xl shadow-md p-4 flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <p className="text-gray-600">
                        <span className="font-bold text-rdi-blue">{selectedTimesheets.length}</span> timesheet(s) selected
                    </p>
                    <button
                        onClick={() => navigate('/admin/rollback')}
                        className="text-sm text-red-600 hover:text-red-800 underline"
                    >
                        Rollback Exported
                    </button>
                </div>
                <Button
                    onClick={handleExport}
                    loading={syncingAction === 'export'}
                    disabled={selectedTimesheets.length === 0 || !isQboConnected}
                >
                    {syncingAction === 'export' ? 'Exporting...' : 'Export Selected to QuickBooks'}
                </Button>
            </div>

            {/* Recent Sync Activity */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                <div className="px-4 py-3 border-b">
                    <h2 className="font-semibold text-gray-800">Recent Sync Activity</h2>
                </div>
                {(!status?.recentLogs || status.recentLogs.filter(l => !l.commandType?.includes('debug') && !l.commandType?.includes('estimate_debug') && !l.commandType?.includes('time_activity') && !l.commandType?.includes('qbo_post_error') && !l.commandType?.includes('estimate_payload_debug')).length === 0) ? (
                    <div className="text-center py-8 text-gray-500">No recent activity</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Time</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Action</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Status</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Records</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Message</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                    {status.recentLogs
                                        .filter(log => !log.commandType?.includes('debug') && !log.commandType?.includes('estimate_debug') && !log.commandType?.includes('time_activity') && !log.commandType?.includes('qbo_post_error') && !log.commandType?.includes('estimate_payload_debug'))
                                    .map((log, index) => (
                                        <tr key={index} className={log.status === 'failed' || log.status === 'error' ? 'bg-red-50' : ''}>
                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                                {new Date(log.createdAt).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium">
                                                {log.commandType?.replace(/_/g, ' ')}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs px-2 py-1 rounded font-medium ${log.status === 'success' || log.status === 'completed'
                                                        ? 'bg-green-100 text-green-800'
                                                        : log.status === 'failed' || log.status === 'error'
                                                            ? 'bg-red-100 text-red-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{log.recordsAffected}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{log.message || '-'}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Error Logs */}
            <div className="bg-white rounded-xl shadow-md p-4 flex items-center justify-between">
                <div>
                    <p className="font-semibold text-gray-800">Error Logs</p>
                    <p className="text-sm text-gray-500">View detailed error history</p>
                </div>
                <Button variant="secondary" onClick={() => navigate('/admin/logs')}>
                    📋 View Error Logs
                </Button>
            </div>
        </div>
    )
}